import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  Button,
  Card,
  CenteredLoader,
  ModuleTitle,
} from '@/components_deprecated'
import styled from 'styled-components'
import type { AbsenceQuery } from './types.graphql'
import type { IdVariable } from '@/types/graphql'
import { ABSENCE_QUERY } from './queries'
import {
  useAdmin,
  useBreadcrumbs,
  useModuleResponsible,
  useOnErrorAuto,
  useParam,
} from '@/utils'
import { format } from '@/utils/date-fns'
import { AbsenceStatusDisplay } from './components/AbsenceStatusDisplay'
import { Loading } from './components/Loading'
import { AbsenceInfoBox } from './components/AbsenceInfoBox'
import { useEffect, useMemo } from 'react'
import { useAbsenceHandler } from './components/handler.hooks'
import { Module } from '@/modules/companies'
import { isMobileOnly } from 'react-device-detect'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

const ContentWrapper = styled(Card)`
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-areas:
    'status start end user type'
    'reason reason reason reason reason'
    'button button button button button';

  background: ${props => props.theme.colors.quaternary};
  padding: 0;

  ${props => props.theme.media.custom({ max: '850px' })} {
    overflow-x: auto;
  }

  ${props => props.theme.media.mobile} {
    display: grid;
    grid-template-columns: 1fr 1 fr;
    grid-template-areas:
      'status user'
      'start end'
      'type type'
      'reason reason'
      'button button';
  }
`

const ButtonWrapper = styled.div`
  grid-area: button;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding: 0 60px 40px 0;
  box-sizing: border-box;

  ${isMobileOnly &&
  `
    padding: 0 0 40px;
    justify-content: center;
  `}
`

const StyledButton = styled(Button)`
  ${isMobileOnly &&
  `
   width: 100%;
  `}
`

export const Absence = () => {
  const translations = useTranslate({
    loading: 'common.loading',
    start: 'common.start',
    end: 'common.end',
    absences: 'common.absences',
    employee: 'common.employee',
    status: 'common.status',
    reason: 'common.reason',
    absenceType: 'timesheets.absence-type',
    editAbsenceStatus: 'timesheets.edit-absence-status',
  })

  const onErrorAuto = useOnErrorAuto()
  const { setOverride, overrides } = useBreadcrumbs()
  const absenceId = useParam('absenceId')

  const { editStatus } = useAbsenceHandler()
  const admin = useAdmin()
  const { isResponsibleUser } = useModuleResponsible(Module.ABSENCES)
  const canEditAbsenceStatus = admin || isResponsibleUser

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery<AbsenceQuery, IdVariable>(ABSENCE_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: absenceId,
    },
    onError: onErrorAuto(),
  })

  const { status, timeStart, timeEnd, employee, absenceType, reason } =
    useMemo(() => {
      // returns the value if it exists, or '-' if null/undefined.
      // If transform is passed, it applies it
      const notUndef = <T,>(
        value: T | null | undefined,
        transform?: (v: T) => T
      ) => (value == null ? '-' : transform ? transform(value) : value)

      const absence = data?.absence

      return {
        status: absence?.requestStatus,
        timeStart: notUndef(absence?.datetimeStart, val =>
          format(new Date(val), 'dd.MM.yyyy')
        ),
        timeEnd: notUndef(absence?.datetimeEnd, val =>
          format(new Date(val), 'dd.MM.yyyy')
        ),
        employee: notUndef(absence?.user?.fullName),
        absenceType: notUndef(absence?.absenceType?.name),
        reason: notUndef(absence?.reason),
      }
    }, [data])

  useEffect(() => {
    if (
      !data?.absence?.user?.fullName &&
      overrides[absenceId] !== translations.loading
    )
      setOverride(absenceId, translations.loading)
    else if (
      data?.absence.user?.fullName &&
      overrides[absenceId] === translations.loading
    ) {
      setOverride(absenceId, data?.absence.user?.fullName)
    }
  }, [setOverride, data, translations.loading, absenceId, overrides])

  if (queryLoading) {
    return (
      <Loading>
        <CenteredLoader marginTop="0" size={72} thickness={8} />
      </Loading>
    )
  }

  return (
    <Wrapper>
      <ModuleTitle>{translations.absences}</ModuleTitle>
      <ContentWrapper>
        <AbsenceInfoBox
          area="status"
          title={translations.status}
          borderBottom
          value={status ? <AbsenceStatusDisplay status={status} /> : '-'}
          className="status"
        />
        <AbsenceInfoBox
          area="start"
          title={translations.start}
          borderLeft
          borderBottom
          value={timeStart}
          className="time"
        />
        <AbsenceInfoBox
          area="end"
          title={translations.end}
          borderLeft
          borderBottom
          value={timeEnd}
          className="time"
        />
        <AbsenceInfoBox
          area="user"
          title={translations.employee}
          borderLeft
          borderBottom
          value={employee}
        />
        <AbsenceInfoBox
          area="type"
          title={translations.absenceType}
          borderLeft
          borderBottom
          value={absenceType}
        />
        <AbsenceInfoBox
          area="reason"
          title={translations.reason}
          value={reason}
        />
        {data?.absence && canEditAbsenceStatus && (
          <ButtonWrapper>
            <StyledButton
              onClick={() => editStatus(data?.absence, undefined, refetch)}
            >
              {translations.editAbsenceStatus}
            </StyledButton>
          </ButtonWrapper>
        )}
      </ContentWrapper>
    </Wrapper>
  )
}
